'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { trackSearch } from '@/lib/analytics/ga4';

interface SearchProduct {
  product_id: number;
  name: string;
  slug: string;
  short_description: string;
  base_price: number;
  rating: number;
  review_count: number;
  is_on_sale: boolean;
  sale_price: number | null;
  category_name: string;
  category_slug: string;
  primary_image: string | null;
}

interface SearchResponse {
  products: SearchProduct[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  query: string;
}

export default function SearchPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [categories, setCategories] = useState<{ slug: string; name: string }[]>([]);

  // Fetch categories for filter sidebar
  useEffect(() => {
    fetch('/api/v2/commerce/categories')
      .then(res => res.json())
      .then(data => {
        const cats = data.data?.categories || data.data || [];
        setCategories(cats.map((c: any) => ({ slug: c.slug, name: c.name })));
      })
      .catch(() => {});
  }, []);

  // Fetch search results
  const fetchResults = useCallback(async (searchQuery: string, pg: number) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setTotal(0);
      setTotalPages(0);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: searchQuery.trim(),
        page: String(pg),
        limit: '20',
        sort: sortBy,
      });
      if (category) params.set('category', category);
      if (minPrice) params.set('min_price', minPrice);
      if (maxPrice) params.set('max_price', maxPrice);

      const res = await fetch(`/api/v2/search/products?${params}`);
      if (res.ok) {
        const data = await res.json();
        const d: SearchResponse = data.data || data;
        setResults(d.products || []);
        setTotal(d.total || 0);
        setTotalPages(d.totalPages || 0);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, category, minPrice, maxPrice]);

  // Search when query/filters change
  useEffect(() => {
    setPage(1);
    fetchResults(initialQuery, 1);
  }, [initialQuery, fetchResults]);

  // Search when page changes
  useEffect(() => {
    if (page > 1) fetchResults(initialQuery, page);
  }, [page, initialQuery, fetchResults]);

  // Handle new search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      trackSearch(query.trim());
      router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  // Apply filters
  const applyFilters = () => {
    setPage(1);
    fetchResults(initialQuery, 1);
    setShowMobileFilters(false);
  };

  const clearFilters = () => {
    setCategory('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('relevance');
    setPage(1);
  };

  const hasActiveFilters = category || minPrice || maxPrice;

  return (
    <div className="min-h-screen bg-sb">
      {/* Search Header */}
      <div className="bg-charcoal text-white">
        <div className="container mx-auto px-4 py-8">
          <nav className="text-sm text-neutral-300 mb-4">
            <Link href="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Search</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-bold mb-4">
            {initialQuery ? (
              <>Search Results for &ldquo;{initialQuery}&rdquo;</>
            ) : (
              'Search Products'
            )}
          </h1>
          {/* Search bar */}
          <form onSubmit={handleSearch} className="max-w-2xl relative">
            <input maxLength={80}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search blinds, shades, shutters..."
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sb-gold focus:bg-white/20 text-base"
            />
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-sb-gold hover:bg-sb-gold-hover text-black font-semibold px-4 py-1.5 rounded-md text-sm transition-colors">
              Search
            </button>
          </form>
          {total > 0 && (
            <p className="text-neutral-300 mt-3 text-sm">{total} {total === 1 ? 'result' : 'results'} found</p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex items-center gap-2 w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-5 w-5" />
            <span className="font-medium">Filters & Sort</span>
            {hasActiveFilters && (
              <span className="ml-auto bg-sb-gold text-black text-xs px-2 py-1 rounded-full font-bold">Active</span>
            )}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className={`lg:w-64 lg:block ${showMobileFilters ? 'block' : 'hidden'} bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:sticky lg:top-20 lg:self-start`}>
            <div className="lg:hidden flex justify-between items-center mb-4 pb-4 border-b">
              <h2 className="text-lg font-bold text-heading-charcoal">Filters</h2>
              <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Sort */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sb-gold"
              >
                <option value="relevance">Relevance</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
              </select>
            </div>

            {/* Category Filter */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Category</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setCategory('')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${!category ? 'bg-sb-gold text-black font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                >
                  All Categories
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setCategory(cat.slug)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${category === cat.slug ? 'bg-sb-gold text-black font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Price Range</h3>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="Min"
                  className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="Max"
                  className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            {/* Apply / Clear */}
            <div className="space-y-2">
              <button
                onClick={applyFilters}
                className="w-full px-4 py-2 bg-sb-gold hover:bg-sb-gold-hover text-black font-semibold rounded-lg text-sm transition-colors"
              >
                Apply Filters
              </button>
              {hasActiveFilters && (
                <button
                  onClick={() => { clearFilters(); setTimeout(() => fetchResults(initialQuery, 1), 0); }}
                  className="w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1">
            {/* Loading */}
            {loading && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 animate-pulse">
                    <div className="aspect-square bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                      <div className="h-6 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Results */}
            {!loading && initialQuery && results.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                  <p className="text-gray-600 mb-6">
                    We couldn&apos;t find any products matching &ldquo;{initialQuery}&rdquo;. Try a different search term or browse our categories.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link href="/products" className="px-6 py-2 bg-sb-gold text-black rounded-lg font-semibold hover:bg-sb-gold-hover transition-colors">
                      Browse All Products
                    </Link>
                    {hasActiveFilters && (
                      <button
                        onClick={() => { clearFilters(); setTimeout(() => fetchResults(initialQuery, 1), 0); }}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Empty state - no query */}
            {!loading && !initialQuery && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Search our products</h3>
                  <p className="text-gray-600">
                    Enter a search term above to find blinds, shades, shutters, and more.
                  </p>
                </div>
              </div>
            )}

            {/* Product Grid */}
            {!loading && results.length > 0 && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {results.map((product) => (
                    <Link
                      key={product.product_id}
                      href={`/products/configure/${product.slug}`}
                      className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      <div className="relative aspect-square bg-gray-100 overflow-hidden">
                        {product.primary_image ? (
                          <Image
                            src={product.primary_image}
                            alt={product.name}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="bg-gray-100 w-full h-full flex items-center justify-center">
                            <span className="text-gray-400 text-4xl">&#x1FA9F;</span>
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <span className="inline-block bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                            {product.category_name}
                          </span>
                        </div>
                        {product.is_on_sale && product.sale_price && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                              SALE
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-base text-heading-charcoal mb-1 line-clamp-2 group-hover:text-sb-gold transition-colors">
                          {product.name}
                        </h3>
                        {product.rating > 0 && (
                          <div className="flex items-center gap-1 mb-2">
                            <Star size={14} className="text-yellow-400 fill-yellow-400" />
                            <span className="text-xs text-gray-600">{product.rating.toFixed(1)} ({product.review_count})</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                          <div>
                            {/* Only the real starting price (36"×60", from the pricing
                                engine). `base_price` is an internal input and the old
                                strike-through against it was a fabricated discount. */}
                            {Number((product as any).starting_price) > 0 ? (
                              <>
                                <p className="text-xs text-gray-500">From</p>
                                <p className="text-lg font-bold text-heading-charcoal">${Number((product as any).starting_price).toFixed(2)}</p>
                              </>
                            ) : (
                              <p className="text-xs text-gray-500">Priced to your size</p>
                            )}
                          </div>
                          <span className="text-sm font-medium text-primary-red">Configure</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            page === pageNum
                              ? 'bg-sb-gold text-black'
                              : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
